import { pgTable, text, boolean, timestamp, integer, index, foreignKey, jsonb, check } from 'drizzle-orm/pg-core';
import { sql } from 'drizzle-orm';
import { bonusDurationMode, bonusType } from './enums';
import { channelPass } from './channel-pass';
import { course } from './course';
import { subscriptionPlan } from './subscription-plan';
import { book } from './book';
import { file } from './files';

export const pricingTier = pgTable('PricingTier', {
  id: integer().primaryKey().generatedAlwaysAsIdentity(),

  // Explicit FK — ровно одна не null
  channelPassId: integer(),
  courseId: integer(),
  subscriptionPlanId: integer(),
  bookId: integer(),

  durationDays: integer(), // null = бессрочно

  // Ценообразование — prices единственный source of truth
  prices: jsonb().$type<Record<string, number>>().notNull(),
  baseCurrency: text().default('UZS').notNull(),

  // Промо-цены (временная акция)
  promoPrices: jsonb().$type<Record<string, number>>(),
  promoUntil: timestamp({ precision: 3, mode: 'date' }),

  // Семейный тариф
  isFamily: boolean().default(false).notNull(),
  familySlots: integer().default(0).notNull(), // 0 = не семейный

  isActive: boolean().default(true).notNull(),
  order: integer().default(0).notNull(),

  createdAt: timestamp({ precision: 3, mode: 'date' }).default(sql`CURRENT_TIMESTAMP`).notNull(),
  updatedAt: timestamp({ precision: 3, mode: 'date' }).$onUpdate(() => new Date()).notNull(),

  // Изображение (для абонементов — отображается в карточке статуса)
  imageId: integer(),

  // Градиент для UI
  gradientFrom: text(),
  gradientTo: text(),
  gradientAngle: integer().default(180),

  // Целевая языковая аудитория (пустой массив = видно всем)
  targetLanguages: jsonb().$type<string[]>().notNull().default(sql`'[]'::jsonb`),

  // Внешний вид карточки абонемента (light/dark темы), null = дефолтные цвета на платформе.
  // Структура совпадает с MembershipCardAppearance из @masters-club/types.
  cardAppearance: jsonb().$type<{
    light: { bg: string; patternPrimary: string; patternSecondary: string };
    dark: { bg: string; patternPrimary: string; patternSecondary: string };
  } | null>(),
}, (table) => [
  // CHECK: ровно одна FK не null
  check('pricing_tier_one_fk_check',
    sql`num_nonnulls(${table.channelPassId}, ${table.courseId}, ${table.subscriptionPlanId}, ${table.bookId}) = 1`
  ),
  // CHECK: baseCurrency присутствует в prices
  check('pricing_tier_base_currency_check',
    sql`${table.prices}->${table.baseCurrency} IS NOT NULL`
  ),
  index('PricingTier_channelPassId_idx').using('btree', table.channelPassId.asc().nullsLast()),
  index('PricingTier_courseId_idx').using('btree', table.courseId.asc().nullsLast()),
  index('PricingTier_subscriptionPlanId_idx').using('btree', table.subscriptionPlanId.asc().nullsLast()),
  index('PricingTier_bookId_idx').using('btree', table.bookId.asc().nullsLast()),
  index('PricingTier_isActive_idx').using('btree', table.isActive.asc().nullsLast()),
  index('PricingTier_order_idx').using('btree', table.order.asc().nullsLast()),
  index('PricingTier_targetLanguages_idx').using('gin', table.targetLanguages),
  foreignKey({
    columns: [table.channelPassId],
    foreignColumns: [channelPass.id],
    name: 'PricingTier_channelPassId_fkey'
  }).onUpdate('cascade').onDelete('cascade'),
  foreignKey({
    columns: [table.courseId],
    foreignColumns: [course.id],
    name: 'PricingTier_courseId_fkey'
  }).onUpdate('cascade').onDelete('cascade'),
  foreignKey({
    columns: [table.subscriptionPlanId],
    foreignColumns: [subscriptionPlan.id],
    name: 'PricingTier_subscriptionPlanId_fkey'
  }).onUpdate('cascade').onDelete('cascade'),
  foreignKey({
    columns: [table.bookId],
    foreignColumns: [book.id],
    name: 'PricingTier_bookId_fkey'
  }).onUpdate('cascade').onDelete('cascade'),
  foreignKey({
    columns: [table.imageId],
    foreignColumns: [file.id],
    name: 'PricingTier_imageId_fkey'
  }).onUpdate('cascade').onDelete('set null'),
]);

export const bonusTemplate = pgTable('BonusTemplate', {
  id: integer().primaryKey().generatedAlwaysAsIdentity(),
  tierId: integer().notNull(),

  type: bonusType().notNull(),
  initialValue: jsonb().notNull(), // 3 | true | [] | "ссылка"

  durationMode: bonusDurationMode().default('INHERIT').notNull(),
  durationDays: integer(), // для RELATIVE
  expiresAt: timestamp({ precision: 3, mode: 'date' }), // для ABSOLUTE

  order: integer().default(0).notNull(),

  createdAt: timestamp({ precision: 3, mode: 'date' }).default(sql`CURRENT_TIMESTAMP`).notNull(),
  updatedAt: timestamp({ precision: 3, mode: 'date' }).$onUpdate(() => new Date()).notNull(),
}, (table) => [
  index('BonusTemplate_tierId_idx').using('btree', table.tierId.asc().nullsLast()),
  index('BonusTemplate_order_idx').using('btree', table.order.asc().nullsLast()),
  foreignKey({
    columns: [table.tierId],
    foreignColumns: [pricingTier.id],
    name: 'BonusTemplate_tierId_fkey'
  }).onUpdate('cascade').onDelete('cascade'),
]);
